import { Injectable } from '@angular/core';
import dayjs from '../dayjs';
import { Observable } from 'rxjs';

export const IMAGE_FILE_TYPES = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
export const PDF_FILE_TYPES = ['.pdf'];
export const EXCEL_FILE_TYPES = ['.xlsx'];
export const ALL_FILE_TYPES = [
  ...IMAGE_FILE_TYPES,
  ...PDF_FILE_TYPES,
  ...EXCEL_FILE_TYPES,
  '.docx',
  '.doc',
  '.xls',
  '.pptx',
  '.ppt',
  '.mp4',
  '.mov',
];

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  isPrimitiveType(value: any): boolean {
    return ['boolean', 'number', 'string'].includes(typeof value);
  }

  convertImageToUrl(file: File): Observable<string | ArrayBuffer> {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Observable((subscriber) => {
      reader.onload = (event) => {
        subscriber.next(event.target.result);
      };
    });
  }

  toFormData(obj: { [k: string]: number | boolean | string | Blob }): FormData {
    const formData = new FormData();
    Object.keys(obj).forEach((key) => {
      formData.append(
        key,
        obj[key] instanceof Blob ? (obj[key] as Blob) : String(obj[key])
      );
    });
    return formData;
  }

  formatDateTimeWithLength(
    date: string,
    startTime: string,
    endTime: string
  ): string {
    const dateObj = dayjs(date);
    const startObj = dayjs(`${date} ${startTime}`);
    let endObj = dayjs(`${date} ${endTime}`);
    if (endObj < startObj) {
      endObj = endObj.add(1, 'day');
    }
    const hourDiff = endObj.diff(startObj, 'hour');
    const minuteDiff = endObj.diff(startObj, 'minute') % 60;
    const hourStr =
      hourDiff > 1 ? `${hourDiff} hrs ` : hourDiff > 0 ? '1 hr ' : '';
    const minuteStr =
      minuteDiff > 1
        ? `${minuteDiff} mins`
        : minuteDiff > 0
        ? `${minuteDiff} min`
        : '';
    const hourMinuteStr = `${hourStr}${minuteStr}`.trim();
    return `${dateObj.format('D MMMM YYYY')} ${startObj.format(
      'HH:mm'
    )}-${endObj.format('HH:mm')} (${hourMinuteStr})`;
  }

  calculateHours(startTime: string, endTime: string): number {
    // Convert time strings to Date objects
    const start = dayjs(`1970-01-01T${startTime}`);
    let end = dayjs(`1970-01-01T${endTime}`);

    // If end time is less than start time, it means end is on the next day
    if (end < start) {
      end = end.add(1, 'day');
    }

    // Calculate the difference in hours
    return end.diff(start).valueOf() / 1000 / 60 / 60;
  }

  loadFilterFromLocalStorage(moduleName: string, subModuleName = '') {
    if (localStorage.getItem('moduleName') === moduleName) {
      const valueFilter = JSON.parse(
        localStorage.getItem(
          'valueFilter' + (subModuleName ? subModuleName : '')
        )
      );
      const valueCustomFilter = JSON.parse(
        localStorage.getItem(
          'valueCustomFilter' + (subModuleName ? subModuleName : '')
        )
      );
      localStorage.setItem('subModuleName', subModuleName);
      // Return the list
      return {
        params: valueFilter,
        customFilter: valueCustomFilter,
      };
    } else {
      localStorage.setItem('subModuleName', subModuleName);
      localStorage.setItem('moduleName', moduleName);
      // Remove all items that contain 'valueFilter' or 'valueCustomFilter' in their keys
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('valueFilter') || key.includes('valueCustomFilter')) {
          localStorage.removeItem(key);
        }
      });
      return { params: null, customFilter: null };
    }
  }

  saveFilterToLocalStorage(params: any, customFilter: any, subModuleName = '') {
    localStorage.setItem(
      'valueFilter' + (subModuleName ? subModuleName : ''),
      JSON.stringify(params)
    );
    localStorage.setItem(
      'valueCustomFilter' + (subModuleName ? subModuleName : ''),
      JSON.stringify(customFilter)
    );
  }

  removeFilterFromLocalStorage() {
    ['valueFilter', 'valueCustomFilter', 'moduleName', 'subModuleName'].forEach(
      (key) => localStorage.removeItem(key)
    );
  }
}
